import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { AlertTriangle, ArrowUpRight, ArrowDownRight, Wallet } from "lucide-react";
import { getPortfolio } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, Overline, MetricCard, Delta, Loader, Badge } from "../components/Primitives";
import { AC_COLORS, POS, NEG, fmtPct } from "../lib/format";

const RANGES = { "1M": 1, "6M": 6, "1Y": 12, "5Y": 60, "10Y": 120, "MAX": 999 };

export default function Overview() {
  const { money, currency, usdInr } = useApp();
  const { data, isLoading } = useQuery({ queryKey: ["portfolio"], queryFn: getPortfolio, refetchInterval: 45000 });
  const [range, setRange] = useState("1Y");

  const history = useMemo(() => {
    if (!data) return [];
    const n = RANGES[range];
    const arr = data.history.slice(-Math.min(n, data.history.length));
    return arr.map((d) => ({ ...d, disp: currency === "USD" ? d.value / usdInr : d.value }));
  }, [data, range, currency, usdInr]);

  if (isLoading || !data) return <Loader />;

  const { metrics, allocation, drift_alerts, recent_orders, total_value_inr, total_pnl_inr, total_pnl_pct } = data;
  const donut = allocation.filter((a) => a.value_inr > 0).map((a) => ({ name: a.label, value: a.value_inr, ac: a.asset_class }));
  const domestic = allocation.filter((a) => ["domestic_equity", "sovereign_debt", "gold_commodity", "cash"].includes(a.asset_class)).reduce((s, a) => s + a.weight, 0);
  const global = allocation.some((a) => a.weight > 0) ? 100 - domestic : 0;

  return (
    <div className="space-y-5">
      {drift_alerts.length > 0 && (
        <Card glow className="p-3.5 flex items-start gap-3" testid="rebalance-alert-banner">
          <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: "rgba(255,179,0,0.14)" }}>
            <AlertTriangle className="w-4 h-4 text-[#ffb300]" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-[#f8fafc]">Rebalance Alert · {drift_alerts.length} asset {drift_alerts.length > 1 ? "classes" : "class"} drifting beyond ±5%</div>
            <div className="text-xs text-[#94a3b8] mt-1 flex flex-wrap gap-x-4 gap-y-1">
              {drift_alerts.map((a) => (
                <span key={a.asset_class} className="font-mono">
                  {a.label}: <span style={{ color: a.drift > 0 ? NEG : "#ffb300" }}>{fmtPct(a.drift)}</span> ({a.action})
                </span>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Net worth header + metrics */}
      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 p-5" testid="networth-card">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <Overline>Total Portfolio Value</Overline>
              <div className="font-mono tabular text-3xl lg:text-4xl font-semibold mt-1" data-testid="net-worth-value">
                {money(total_value_inr)}
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-sm">
                <span className="font-mono" style={{ color: total_pnl_inr >= 0 ? POS : NEG }}>
                  {total_pnl_inr >= 0 ? <ArrowUpRight className="inline w-4 h-4" /> : <ArrowDownRight className="inline w-4 h-4" />}
                  {money(Math.abs(total_pnl_inr), { compact: true })} · {fmtPct(total_pnl_pct)}
                </span>
                <span className="text-xs text-[#64748b]">unrealized P&amp;L</span>
              </div>
            </div>
            <div className="flex rounded-lg border border-[#2d3c59] overflow-hidden">
              {Object.keys(RANGES).map((r) => (
                <button key={r} data-testid={`range-${r}`} onClick={() => setRange(r)}
                  className={`px-2.5 py-1 font-mono text-[11px] transition-colors ${range === r ? "bg-[#1a2338] text-[#00e5ff]" : "text-[#64748b] hover:text-[#94a3b8]"}`}>{r}</button>
              ))}
            </div>
          </div>
          <div className="h-[260px] mt-4 -ml-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={history}>
                <defs>
                  <linearGradient id="nw" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00e5ff" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#00e5ff" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10, fontFamily: "IBM Plex Mono" }} tickFormatter={(d) => d.slice(2, 7)} minTickGap={40} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10, fontFamily: "IBM Plex Mono" }} tickFormatter={(v) => money(currency === "USD" ? v * usdInr : v, { compact: true })} width={64} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#0f1420", border: "1px solid #2d3c59", borderRadius: 8, fontFamily: "IBM Plex Mono", fontSize: 12 }}
                  labelStyle={{ color: "#94a3b8" }} formatter={(v) => [money(currency === "USD" ? v * usdInr : v), "Value"]} />
                <Area type="monotone" dataKey="disp" stroke="#00e5ff" strokeWidth={2} fill="url(#nw)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5" testid="allocation-card">
          <Overline>Asset Class Breakdown</Overline>
          <div className="flex items-center justify-between mt-1">
            <span className="text-sm text-[#94a3b8]">Domestic vs Global</span>
            <span className="font-mono text-xs"><span className="text-[#00e5ff]">{domestic.toFixed(0)}%</span> / <span className="text-[#818cf8]">{global.toFixed(0)}%</span></span>
          </div>
          <div className="h-[180px] relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={donut} dataKey="value" innerRadius={55} outerRadius={82} paddingAngle={2} stroke="none">
                  {donut.map((d) => <Cell key={d.ac} fill={AC_COLORS[d.ac]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0f1420", border: "1px solid #2d3c59", borderRadius: 8, fontFamily: "IBM Plex Mono", fontSize: 12 }}
                  formatter={(v, n) => [money(v, { compact: true }), n]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5 mt-2">
            {allocation.filter((a) => a.value_inr > 0).map((a) => (
              <div key={a.asset_class} className="flex items-center gap-2 text-xs" data-testid={`alloc-row-${a.asset_class}`}>
                <span className="w-2 h-2 rounded-sm" style={{ background: AC_COLORS[a.asset_class] }} />
                <span className="text-[#94a3b8] flex-1">{a.label}</span>
                <span className="font-mono text-[#f8fafc]">{a.weight.toFixed(1)}%</span>
                <span className="font-mono w-14 text-right" style={{ color: Math.abs(a.drift) >= 5 ? "#ffb300" : "#64748b" }}>{fmtPct(a.drift, 1)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Health scorecard */}
      <div>
        <Overline className="mb-2">Health Scorecard</Overline>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <MetricCard testid="metric-sharpe" label="Sharpe Ratio" value={metrics.empty ? "—" : metrics.sharpe} accent="#00e676" delay={0.02} />
          <MetricCard testid="metric-beta" label="Portfolio Beta" value={metrics.empty ? "—" : metrics.beta} accent="#00e5ff" delay={0.04} />
          <MetricCard testid="metric-divyield" label="Dividend Yield" value={metrics.empty ? "—" : `${metrics.dividend_yield}%`} delay={0.06} />
          <MetricCard testid="metric-cagr" label="Exp. 10Y CAGR" value={metrics.empty ? "—" : `${metrics.cagr_10y}%`} accent="#00e676" delay={0.08} />
          <MetricCard testid="metric-maxdd" label="Max Drawdown" value={metrics.empty ? "—" : `${metrics.max_drawdown}%`} accent="#ff3b30" delay={0.1} />
          <MetricCard testid="metric-vol" label="Volatility" value={metrics.empty ? "—" : `${metrics.volatility}%`} accent="#ffb300" delay={0.12} />
        </div>
      </div>

      {/* Recent orders */}
      <Card className="p-5" testid="recent-orders-card">
        <div className="flex items-center gap-2 mb-3">
          <Wallet className="w-4 h-4 text-[#00e5ff]" />
          <Overline>Recent Executed Orders &amp; Realized P&amp;L</Overline>
        </div>
        <div className="scroll-x">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="text-left overline border-b border-[#1e293b]">
                <th className="pb-2 font-medium">Date</th><th className="pb-2 font-medium">Side</th><th className="pb-2 font-medium">Instrument</th>
                <th className="pb-2 font-medium text-right">Qty</th><th className="pb-2 font-medium text-right">Value</th><th className="pb-2 font-medium text-right">Realized P&amp;L</th>
              </tr>
            </thead>
            <tbody className="font-mono tabular">
              {recent_orders.map((o, i) => (
                <tr key={i} className="border-b border-[#131a2a]" data-testid={`order-row-${i}`}>
                  <td className="py-2.5 text-[#64748b]">{o.date}</td>
                  <td className="py-2.5"><Badge color={o.side === "BUY" ? POS : NEG}>{o.side}</Badge></td>
                  <td className="py-2.5 text-[#f8fafc]">{o.name}</td>
                  <td className="py-2.5 text-right text-[#94a3b8]">{o.qty}</td>
                  <td className="py-2.5 text-right text-[#94a3b8]">{money(o.value_inr, { compact: true })}</td>
                  <td className="py-2.5 text-right" style={{ color: o.pnl_inr == null ? "#64748b" : o.pnl_inr >= 0 ? POS : NEG }}>
                    {o.pnl_inr == null ? "—" : `${o.pnl_inr >= 0 ? "+" : ""}${money(o.pnl_inr, { compact: true })}`}
                  </td>
                </tr>
              ))}
              {recent_orders.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-xs text-[#64748b] font-sans" data-testid="orders-empty">No transactions yet — add your first investment in Portfolio.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
